import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils/servers';

export interface LogisticOperatorApi {
  id: string;
  name: string;
  code?: string;
}

export interface CommerceApi {
  id: string;
  name: string;
  code?: string;
}

export interface BranchApi {
  id: string;
  name: string;
  businessName?: string;
  commerceId: string;
  address?: string;
  addressId?: string;
  externalId?: string;
}

export interface CommercialServiceApi {
  id: string;
  name: string;
  description?: string;
  commercialName?: string;
  externalId?: string;
}

export interface CountryApi {
  id: string;
  name: string;
  code?: string;
}

export interface DepartmentApi {
  id: string;
  name: string;
  code?: string;
}

export interface CityApi {
  id: string;
  name: string;
  departmentId: string;
  stateOrDepartmentId?: string;
}

export interface NeighborhoodApi {
  id: string;
  name: string;
  cityId: string;
}

export interface ProductTypeApi {
  id: string;
  name: string;
  description?: string;
}

export interface StatusApi {
  id: string;
  name: string;
  color?: string;
}

export interface SizeApi {
  id: string;
  name: string;
  description?: string;
}

export interface ClassificationApi {
  id: string;
  name: string;
  description?: string;
}

export interface ComplexityApi {
  id: string;
  name: string;
  description?: string;
}

export interface TypeApi {
  id: string;
  name: string;
  description?: string;
}

export interface ChannelApi {
  id: string;
  name: string;
  description?: string;
}

export interface TimeSlotApi {
  id: string;
  name: string;
  startTime?: string;
  endTime?: string;
}

const shippingOptionsService = createApi({
  reducerPath: 'shippingOptionsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: [
    'LogisticOperators',
    'Commerces',
    'Branches',
    'CommercialServices',
    'Countries',
    'Departments',
    'Cities',
    'Neighborhoods',
    'ProductTypes',
    'Statuses',
    'Sizes',
    'Classifications',
    'Complexities',
    'Types',
    'Channels',
    'TimeSlots',
  ],
  endpoints: (builder) => ({
    getCountries: builder.query<
      PaginatedResponse<CountryApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/countries',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: (_, __, { search }) => [
        { type: 'Countries', id: search || 'all' },
        'Countries',
      ],
    }),

    getLogisticOperators: builder.query<
      PaginatedResponse<LogisticOperatorApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/logistic-operator',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: ['LogisticOperators'],
    }),

    getCommerces: builder.query<
      PaginatedResponse<CommerceApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/commerces',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: ['Commerces'],
    }),

    getBranches: builder.query<
      PaginatedResponse<BranchApi>,
      { paginated: PaginatedRequest; commerceId?: string; search?: string }
    >({
      query: ({ paginated, commerceId, search }) => ({
        url: '/branches',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(commerceId && { commerceId }),
          ...(search && { search }),
        },
      }),
      providesTags: ['Branches'],
    }),

    getCommercialServices: builder.query<
      PaginatedResponse<CommercialServiceApi>,
      { paginated: PaginatedRequest; commerceId?: string; search?: string }
    >({
      query: ({ paginated, commerceId, search }) => ({
        url: '/commercial-service',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(commerceId && { commerceId }),
          ...(search && { search }),
        },
      }),
      providesTags: ['CommercialServices'],
    }),

    getDepartments: builder.query<
      PaginatedResponse<DepartmentApi>,
      { paginated: PaginatedRequest; countryId?: string; search?: string }
    >({
      query: ({ paginated, countryId, search }) => ({
        url: '/states-or-departments',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(countryId && { countryId }),
          ...(search && { search }),
        },
      }),
      providesTags: (_, __, { countryId, search }) => [
        { type: 'Departments', id: `${countryId || 'all'}-${search || 'all'}` },
        'Departments',
      ],
    }),

    getCities: builder.query<
      PaginatedResponse<CityApi>,
      { paginated: PaginatedRequest; departmentId?: string; search?: string }
    >({
      query: ({ paginated, departmentId, search }) => {
        if (!departmentId) {
          return {
        url: '/cities',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
              ...(search && { search }),
            },
          };
        }
        return {
          url: `/cities/by-state-or-department/${departmentId}`,
          params: {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
        };
      },
      providesTags: (_, __, { departmentId, search }) => [
        { type: 'Cities', id: `${departmentId || 'all'}-${search || 'all'}` },
        'Cities',
      ],
    }),

    getNeighborhoods: builder.query<
      PaginatedResponse<NeighborhoodApi>,
      { paginated: PaginatedRequest; cityId?: string; search?: string }
    >({
      query: ({ paginated, cityId, search }) => {
        if (!cityId) {
          return {
        url: '/neighborhoods',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
              ...(search && { search }),
            },
          };
        }
        return {
          url: `/neighborhoods/by-city/${cityId}`,
          params: {
            limit: paginated.pageSize,
            offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
        };
      },
      providesTags: (_, __, { cityId, search }) => [
        { type: 'Neighborhoods', id: `${cityId || 'all'}-${search || 'all'}` },
        'Neighborhoods',
      ],
    }),

    getProductTypes: builder.query<
      PaginatedResponse<ProductTypeApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/product-types',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: ['ProductTypes'],
    }),

    getStatuses: builder.query<
      PaginatedResponse<StatusApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/statuses',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Statuses'],
    }),

    getSizes: builder.query<
      PaginatedResponse<SizeApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/shipping-sizes',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Sizes'],
    }),

    getClassifications: builder.query<
      PaginatedResponse<ClassificationApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/classifications',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Classifications'],
    }),

    getComplexities: builder.query<
      PaginatedResponse<ComplexityApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/complexities',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Complexities'],
    }),

    getTypes: builder.query<
      PaginatedResponse<TypeApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/types',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Types'],
    }),

    getChannels: builder.query<
      PaginatedResponse<ChannelApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/channels',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Channels'],
    }),

    getTimeSlots: builder.query<
      PaginatedResponse<TimeSlotApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/time-slots',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['TimeSlots'],
    }),

    searchStreets: builder.query<
      string[],
      { query: string; department: string; city: string }
    >({
      query: ({ query, department, city }) => ({
        url: '/geographic/search-streets',
        params: {
          query: query.trim(),
          department,
          city,
        },
      }),
      transformResponse: (response: unknown): string[] => {
        if (response && typeof response === 'object' && 'data' in response) {
          const data = (response as { data: unknown }).data;
          if (Array.isArray(data)) {
            return data.filter((item): item is string => typeof item === 'string');
          }
        }
        if (Array.isArray(response)) {
          return response.filter((item): item is string => typeof item === 'string');
        }
        return [];
      },
    }),
  }),
});

export default shippingOptionsService;

export const {
  useGetCountriesQuery,
  useGetLogisticOperatorsQuery,
  useGetCommercesQuery,
  useGetBranchesQuery,
  useGetCommercialServicesQuery,
  useGetDepartmentsQuery,
  useGetCitiesQuery,
  useGetNeighborhoodsQuery,
  useGetProductTypesQuery,
  useGetStatusesQuery,
  useGetSizesQuery,
  useGetClassificationsQuery,
  useGetComplexitiesQuery,
  useGetTypesQuery,
  useGetChannelsQuery,
  useGetTimeSlotsQuery,
  useSearchStreetsQuery,
} = shippingOptionsService;
